import { useAlert } from 'dashboard/composables';

const PROFILE_STATUS_REFRESH_EVENT = 'chatwoot:profile-status-refresh';

const reconnectingInboxIds = new Set();

const STATUS_BADGE_CLASSES = {
  success: 'bg-n-teal-2 text-n-teal-11 dark:bg-n-teal-3/20 dark:text-n-teal-11',
  info: 'bg-n-blue-2 text-n-blue-11 dark:bg-n-blue-3/20 dark:text-n-blue-11',
  danger: 'bg-n-ruby-2 text-n-ruby-11 dark:bg-n-ruby-3/20 dark:text-n-ruby-11',
  muted:
    'bg-n-slate-2 text-n-slate-11 dark:bg-n-slate-3/20 dark:text-n-slate-11',
};

const translate = (t, key, fallback) => {
  if (typeof t !== 'function') {
    return fallback;
  }

  try {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const value = t(key);
    return value && value !== key ? value : fallback;
  } catch {
    return fallback;
  }
};

const getSipConnectionManager = () => {
  return (
    window.chatwoot?.plugins?.['sip-channel']?.sipConnectionManager ||
    window.sipConnectionManager
  );
};

const getInboxProviderConfig = inbox => {
  return (
    inbox?.provider_config ||
    inbox?.providerConfig ||
    inbox?.channel?.provider_config ||
    inbox?.channel?.providerConfig ||
    null
  );
};

const dispatchProfileStatusRefresh = () => {
  window.dispatchEvent(new Event(PROFILE_STATUS_REFRESH_EVENT));
};

const getStatusPresentation = (t, sipState) => {
  const statuses = {
    registered: {
      label: translate(t, 'SIP_CHANNEL.STATUS.REGISTERED', 'Registered'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.REGISTERED',
        'Ready to receive or place browser calls.'
      ),
      className: STATUS_BADGE_CLASSES.success,
    },
    connecting: {
      label: translate(t, 'SIP_CHANNEL.STATUS.CONNECTING', 'Connecting'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.CONNECTING',
        'Attempting to register this browser on the SIP server.'
      ),
      className: STATUS_BADGE_CLASSES.info,
    },
    ringing: {
      label: translate(t, 'SIP_CHANNEL.STATUS.RINGING', 'Ringing'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.RINGING',
        'Receiving an incoming SIP call.'
      ),
      className: STATUS_BADGE_CLASSES.info,
    },
    calling: {
      label: translate(t, 'SIP_CHANNEL.STATUS.DIALING', 'Dialing'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.DIALING',
        'Placing an outbound SIP call.'
      ),
      className: STATUS_BADGE_CLASSES.info,
    },
    in_call: {
      label: translate(t, 'SIP_CHANNEL.STATUS.IN_CALL', 'In call'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.IN_CALL',
        'An active SIP call is in progress.'
      ),
      className: STATUS_BADGE_CLASSES.info,
    },
    failed: {
      label: translate(t, 'SIP_CHANNEL.STATUS.INACCESSIBLE', 'Inaccessible'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.INACCESSIBLE',
        'The browser failed to register this SIP inbox.'
      ),
      className: STATUS_BADGE_CLASSES.danger,
    },
    disconnected: {
      label: translate(t, 'SIP_CHANNEL.STATUS.DISCONNECTED', 'Disconnected'),
      detail: translate(
        t,
        'SIP_CHANNEL.STATUS_DETAIL.DISCONNECTED',
        'This browser is not currently registered for SIP calls.'
      ),
      className: STATUS_BADGE_CLASSES.muted,
    },
  };

  return statuses[sipState] || statuses.disconnected;
};

const buildReconnectAction = (t, inbox, currentUser, currentAccountId) => {
  const manager = getSipConnectionManager();

  return {
    label: translate(t, 'SIP_CHANNEL.ACTION.RETRY', 'Retry'),
    loading: reconnectingInboxIds.has(inbox.id),
    onClick: async () => {
      if (!manager?.reconnectInbox || !currentUser?.id || !currentAccountId) {
        useAlert(
          translate(
            t,
            'SIP_CHANNEL.ALERT.RUNTIME_UNAVAILABLE',
            'SIP runtime is not available yet for this account.'
          )
        );
        return;
      }

      reconnectingInboxIds.add(inbox.id);
      dispatchProfileStatusRefresh();

      try {
        await manager.reconnectInbox(inbox, {
          ...currentUser,
          account_id: currentAccountId,
        });
        useAlert(
          translate(
            t,
            'SIP_CHANNEL.ALERT.RECONNECTING',
            'Trying to reconnect your SIP registration.'
          )
        );
      } catch (error) {
        useAlert(
          error?.message ||
            translate(
              t,
              'SIP_CHANNEL.ALERT.RECONNECT_FAILED',
              'Unable to reconnect this SIP registration right now.'
            )
        );
      } finally {
        reconnectingInboxIds.delete(inbox.id);
        dispatchProfileStatusRefresh();
        setTimeout(dispatchProfileStatusRefresh, 1500);
      }
    },
  };
};

export const buildSipProfileStatusSections = payload => {
  if (!payload || !Array.isArray(payload.sections)) {
    return payload;
  }

  const { context = {}, t } = payload;
  const currentUser = context.currentUser;
  const currentAccountId = context.currentAccountId;
  const inboxes = Array.isArray(context.inboxes) ? context.inboxes : [];
  const manager = getSipConnectionManager();

  if (!currentUser?.id || !currentAccountId || !manager) {
    return payload;
  }

  const entries = inboxes
    .filter(inbox => {
      if (inbox.channel_type !== 'Channel::Sip') {
        return false;
      }

      const registrationMode = getInboxProviderConfig(inbox)?.registration_mode;
      return registrationMode === 'per_agent' || !registrationMode;
    })
    .map(inbox => {
      const rawState = manager.getWorker?.(inbox.id)?.getState?.();
      const sipState =
        typeof rawState === 'string' && rawState ? rawState : 'disconnected';
      const presentation = getStatusPresentation(t, sipState);

      return {
        key: `sip:${inbox.id}`,
        label: inbox.name,
        detail: presentation.detail,
        badge: {
          label: presentation.label,
          className: presentation.className,
        },
        action: ['disconnected', 'failed'].includes(sipState)
          ? buildReconnectAction(t, inbox, currentUser, currentAccountId)
          : null,
      };
    });

  if (!entries.length) {
    return payload;
  }

  return {
    ...payload,
    sections: [
      ...payload.sections,
      {
        key: 'sip-channel:registrations',
        title: translate(t, 'SIP_CHANNEL.SECTION_TITLE', 'SIP'),
        entries,
      },
    ],
  };
};

export const registerSipProfileStatusRefreshBridge = () => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const refresh = () => {
    dispatchProfileStatusRefresh();
  };

  window.addEventListener('sip:state-changed', refresh);
  window.addEventListener('sip:error', refresh);
  window.addEventListener('sip:incoming-call', refresh);
  window.addEventListener('sip:call-disconnected', refresh);

  dispatchProfileStatusRefresh();

  return () => {
    window.removeEventListener('sip:state-changed', refresh);
    window.removeEventListener('sip:error', refresh);
    window.removeEventListener('sip:incoming-call', refresh);
    window.removeEventListener('sip:call-disconnected', refresh);
  };
};
